// @ts-nocheck
// 원본 Mantis JS 변환 파일 — Avatar/IconButton/Transitions 등 @extended 컴포넌트 타입 미완.
// 헤더 우측 프로필 메뉴 — Google avatar + 본인 정보 + 알림 설정 + 로그아웃.
//
// 사상: Mantis 스켈레톤의 Profile/Setting 탭 구조 제거.
//   - 프로필 수정/내 정보는 의미 없음 (Google account 정보 + Popper 상단에 이미 표시)
//   - 정말 가치 있는 건 알림 설정 → 바로 노출.

import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ButtonBase from '@mui/material/ButtonBase';
import CardContent from '@mui/material/CardContent';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

import SettingTab from './SettingTab';
import Avatar from 'components/@extended/Avatar';
import MainCard from 'components/MainCard';
import Transitions from 'components/@extended/Transitions';
import IconButton from 'components/@extended/IconButton';
import { useAuth } from 'contexts/AuthContext';

import LogoutOutlined from '@ant-design/icons/LogoutOutlined';

export default function Profile() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const handleToggle = () => setOpen((prev) => !prev);
  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as Node)) return;
    setOpen(false);
  };

  const handleLogout = async () => {
    await signOut();
    setOpen(false);
    navigate('/login', { replace: true });
  };

  const displayName = user?.displayName || user?.email?.split('@')[0] || '게스트';
  const email = user?.email || '로그인되지 않음';
  const initial = (displayName || '?').slice(0, 1).toUpperCase();

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <Tooltip title={displayName} disableInteractive>
        <ButtonBase
          sx={(theme) => ({
            p: 0.25,
            borderRadius: 1,
            '&:focus-visible': { outline: `2px solid ${theme.palette.secondary.dark}`, outlineOffset: 2 }
          })}
          aria-label="open profile"
          ref={anchorRef}
          aria-controls={open ? 'profile-grow' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
        >
          <Avatar
            alt={displayName}
            src={user?.photoURL ?? undefined}
            size="sm"
            sx={{ '&:hover': { outline: '1px solid', outlineColor: 'primary.main' } }}
          >
            {initial}
          </Avatar>
        </ButtonBase>
      </Tooltip>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper
              sx={(theme) => ({
                boxShadow: theme.customShadows?.z1 ?? theme.shadows[6],
                width: 290,
                minWidth: 240,
                maxWidth: { xs: 250, md: 290 }
              })}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard elevation={0} border={false} content={false}>
                  <CardContent sx={{ px: 2.5, pt: 3 }}>
                    <Grid container sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <Grid>
                        <Stack direction="row" sx={{ gap: 1.25, alignItems: 'center' }}>
                          <Avatar
                            alt={displayName}
                            src={user?.photoURL ?? undefined}
                            sx={{ width: 32, height: 32 }}
                          >
                            {initial}
                          </Avatar>
                          <Stack>
                            <Typography variant="h6">{displayName}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {email}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Grid>
                      <Grid>
                        <Tooltip title="로그아웃">
                          <IconButton size="large" sx={{ color: 'text.primary' }} onClick={handleLogout}>
                            <LogoutOutlined />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </CardContent>

                  <Divider />

                  <SettingTab />
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
